/**
 * ───────────────────────────────────────────────────────────────────────────
 * OVERLAY REGISTRY — single source of truth for "is a blocking overlay open?"
 * ───────────────────────────────────────────────────────────────────────────
 * The sitewide floating action stack (Start Now + WhatsApp) must never hover
 * on top of a modal, drawer or mobile filter sheet. Rather than trying to keep
 * a pile of z-index numbers in sync across many files, every full-screen
 * overlay registers itself here while it is mounted/open, and the floating
 * stack simply hides itself whenever the count is greater than zero.
 *
 * Usage inside an overlay component:
 *   useOverlayLock(open);          // open === true while the overlay is shown
 *
 * Usage inside the floating stack:
 *   const blocked = useOverlayOpen();
 *
 * Also exports lockBodyScroll() so overlays can stop the page behind them from
 * scrolling — reference counted, so nested overlays cannot unlock too early.
 */
import { useEffect, useState } from 'react';

let count = 0;
const listeners = new Set<(n: number) => void>();

const emit = () => {
  listeners.forEach((fn) => {
    try {
      fn(count);
    } catch {
      /* a broken listener must never break an overlay */
    }
  });
};

/** Imperative API (for non-React callers). Returns a release function. */
export const acquireOverlay = (): (() => void) => {
  count += 1;
  emit();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    count = Math.max(0, count - 1);
    emit();
  };
};

export const isOverlayOpen = (): boolean => count > 0;

/** Register an overlay while `open` is true. Auto-releases on unmount. */
export const useOverlayLock = (open: boolean): void => {
  useEffect(() => {
    if (!open) return;
    const release = acquireOverlay();
    // Freeze the page behind the overlay (reference counted via the registry).
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      release();
      if (!isOverlayOpen()) document.body.style.overflow = prev || '';
    };
  }, [open]);
};

/** Subscribe to the registry. True while ANY overlay is open. */
export const useOverlayOpen = (): boolean => {
  const [open, setOpen] = useState<boolean>(() => isOverlayOpen());
  useEffect(() => {
    const fn = (n: number) => setOpen(n > 0);
    listeners.add(fn);
    fn(count);
    return () => {
      listeners.delete(fn);
    };
  }, []);
  return open;
};
